// A doorbell for the agent bridge.
//
// When an external agent (Chief of Staff, Front Desk) answers, the API broadcasts
// an `agent_reply` over the sync WebSocket the moment the reply is stored. A chat
// that listens can show it straight away instead of waiting out its next poll —
// which was up to three seconds of a reply sitting there, already written.
//
// It is deliberately only a doorbell: the broadcast goes to every connected client,
// so nothing in it is ever rendered. Listeners get the conversation id and then
// fetch the message over the authenticated API like they always did. Polling stays
// as the fallback, because a socket that quietly dies must not take the chat with it.
// The sync server listens on the API's own host and port. REACT_APP_WS_URL points
// at a /ws path that is not proxied in production (it answers 502), so the socket
// URL is derived from the API base instead — one fewer thing to keep in step.
// REACT_APP_AGENT_WS_URL overrides it if the two ever need to differ.
const WS_URL = process.env.REACT_APP_AGENT_WS_URL || (() => {
  const base = process.env.REACT_APP_API_BASE || '';
  try { const u = new URL(base); return `${u.protocol === 'https:' ? 'wss:' : 'ws:'}//${u.host}`; } catch { return ''; }
})();

class AgentSocket {
  constructor() {
    this.ws = null;
    this.listeners = new Set();
    this.retries = 0;
    this.timer = null;
    // Come straight back when the network returns or the tab is looked at again
    // (a slept Mac used to run out of retries and stay silent — Sep 30 2026).
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.wake());
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.wake(); });
    }
  }

  wake() { if (!this.ws && this.listeners.size) { clearTimeout(this.timer); this.retries = 0; this.connect(); } }

  connect() {
    if (!WS_URL || this.ws || !this.listeners.size) return;
    // The sync server only lets a socket in with the app's session token (?token=),
    // the way the Trello socket sends it — without it this was refused every time.
    let token = null; try { token = JSON.parse(localStorage.getItem('titan_session') || 'null')?.token || null; } catch {}
    if (!token) return;
    try {
      const ws = new WebSocket(WS_URL + '?token=' + encodeURIComponent(token));
      this.ws = ws;
      ws.onopen = () => { this.retries = 0; };
      ws.onmessage = e => {
        let data; try { data = JSON.parse(e.data); } catch { return; }
        if (data && data.type === 'agent_reply') this.listeners.forEach(fn => { try { fn(data); } catch {} });
      };
      // Reconnect while anyone still cares, backing off so a server restart doesn't
      // turn into a reconnect storm. Give up after a while — polling still works.
      ws.onclose = () => {
        this.ws = null;
        if (!this.listeners.size) return;
        this.retries++;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.connect(), Math.min(30000, 2000 * this.retries));
      };
      ws.onerror = () => {};
    } catch { this.ws = null; }
  }

  disconnect() {
    clearTimeout(this.timer);
    const ws = this.ws;
    this.ws = null;
    if (ws) { ws.onclose = null; try { ws.close(); } catch {} }
  }

  // Returns an unsubscribe. The socket opens on the first listener and closes when
  // the last one leaves, so no connection is held open by a screen nobody is on.
  subscribe(fn) {
    this.listeners.add(fn);
    this.connect();
    return () => {
      this.listeners.delete(fn);
      if (!this.listeners.size) this.disconnect();
    };
  }
}

export const agentSocket = new AgentSocket();
